import { useState } from "react";
import { Link } from "react-router-dom";
import Navbar from "@/components/feature/Navbar";
import Footer from "@/components/feature/Footer";
import { categories, faqs } from "@/mocks/anime";

const values = [
  {
    icon: "ri-compass-3-line",
    title: "按题材聚合",
    desc: "以妖精、精灵、妖怪三大题材为主线，把分散的奇幻番剧集中整理，让查找变得简单直接。",
  },
  {
    icon: "ri-filter-3-line",
    title: "宁缺毋滥",
    desc: "收录前严格筛选作品质量与题材契合度，宁缺毋滥，让每一条推荐都有阅读与追看价值。",
  },
  {
    icon: "ri-heart-line",
    title: "持续更新",
    desc: "不断补充新番与冷门佳作，保持内容新鲜，让喜欢奇幻题材的用户总能发现新惊喜。",
  },
];

const principles = [
  "只做题材整理与推荐，不虚构上线信息",
  "热门与冷门并重，照顾不同口味的观众",
  "尊重作品版权，引导用户前往正版平台观看",
  "内容客观中立，保持真实的评分与热度信息",
];

const reviews = [
  {
    name: "糖醋里脊",
    role: "奇幻番剧爱好者",
    text: "第一次找到把妖精、精灵、妖怪题材都聚合起来的站，翻起来太方便了，冷门的妖怪番也收录了不少。",
    avatar: "https://readdy.ai/api/search-image?query=flat%20minimal%20avatar%20illustration%20of%20a%20smiling%20young%20woman%20with%20warm%20coral%20background&width=200&height=200&seq=avt-1&orientation=squarish",
  },
  {
    name: "夜空下的猫",
    role: "追更党",
    text: "追更信息很清晰，哪部更新到第几话一目了然，不用到处翻来翻去，省了不少时间。",
    avatar: "https://readdy.ai/api/search-image?query=flat%20minimal%20avatar%20illustration%20of%20a%20friendly%20young%20man%20with%20teal%20background&width=200&height=200&seq=avt-2&orientation=squarish",
  },
  {
    name: "阿茶",
    role: "二次元收藏家",
    text: "分类和评分都挺客观，推荐的作品质量在线，尤其是妖怪系治愈番，很多都戳中我。",
    avatar: "https://readdy.ai/api/search-image?query=flat%20minimal%20avatar%20illustration%20of%20a%20smiling%20girl%20with%20short%20hair%20and%20magenta%20background&width=200&height=200&seq=avt-3&orientation=squarish",
  },
];

type FormState = "idle" | "loading" | "success" | "error";

export default function About() {
  const [state, setState] = useState<FormState>("idle");
  const [msg, setMsg] = useState("");

  const onSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const honeypot = (fd.get("company_alt") as string) || "";
    if (honeypot.trim()) {
      setState("success");
      setMsg("感谢你的留言，我们会尽快回复你。");
      form.reset();
      return;
    }

    const payload = new URLSearchParams();
    fd.forEach((v, k) => {
      if (k !== "company_alt" && v) payload.append(k, v.toString());
    });

    setState("loading");
    try {
      const res = await fetch("https://readdy.ai/api/form/da21ggjk8r5te5mqsdng", {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: payload.toString(),
      });
      const responseText = await res.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message ||
        parsed?.meta?.detail ||
        (parsed && "message" in parsed ? (parsed as { message?: string }).message : "") ||
        "";
      const isSpam = /spam/i.test(serverMsg) || /spam/i.test(responseText);
      const ok =
        res.ok && (parsed?.code === "OK" || (!parsed?.code && !isSpam));

      if (ok && !isSpam) {
        setState("success");
        setMsg("留言已发送成功，感谢你的支持！");
        form.reset();
      } else {
        setState("error");
        setMsg(serverMsg || "留言发送失败，请稍后再试。");
      }
    } catch {
      setState("error");
      setMsg("网络异常，留言发送失败，请稍后再试。");
    }
  };

  return (
    <>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{
          __html: JSON.stringify([
            {
              "@context": "https://schema.org",
              "@type": "AboutPage",
              name: "关于我们 | 妖精动漫网",
              description: "妖精动漫网的品牌介绍、网站定位、发展理念、内容原则、用户口碑与联系方式。",
              url: "https://updateline.github.io/about",
            },
            {
              "@context": "https://schema.org",
              "@type": "ContactPage",
              name: "联系我们 | 妖精动漫网",
              url: "https://updateline.github.io/about#contact",
            },
          ]),
        }}
      />
      <Navbar />
      <main>
        <section className="relative pt-20 bg-secondary-900 overflow-hidden">
          <div className="absolute inset-0 opacity-20">
            <img
              src="https://readdy.ai/api/search-image?query=soft%20anime%20fantasy%20background%20of%20floating%20fairy%20lights%20and%20paper%20lanterns%20in%20a%20warm%20night%20sky%2C%20minimal%20abstract%2C%20muted%20teal%20and%20coral%20palette&width=1600&height=600&seq=about-hero&orientation=landscape"
              alt="妖精动漫网关于我们背景"
              className="w-full h-full object-cover"
            />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 md:px-6 py-16 md:py-20 text-center">
            <span className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-white/15 text-secondary-100 text-sm border border-white/20">
              <i className="ri-heart-3-line"></i>
              关于妖精动漫网
            </span>
            <h1 className="mt-5 font-heading text-4xl md:text-6xl text-white">让奇幻番剧更好找</h1>
            <p className="mt-4 text-secondary-200 max-w-2xl mx-auto leading-relaxed">
              妖精动漫网是一个专注妖精、精灵、妖怪题材的奇幻番剧聚合站。我们把分散在各大平台的奇幻佳作集中整理，帮你按题材快速找到想看的作品。
            </p>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="max-w-3xl">
              <p className="text-sm text-primary-600 font-medium inline-flex items-center gap-2">
                <i className="ri-building-2-line"></i>
                我们的定位
              </p>
              <h2 className="mt-2 font-heading text-3xl text-foreground-950">网站定位</h2>
              <p className="mt-4 text-foreground-700 leading-8">
                在众多动漫内容中，妖精、精灵、妖怪题材始终是很多观众的心头好，但相关作品分散在各个平台和频道，找起来常常费时费力。
                妖精动漫网正是为了填补这个空白而诞生：我们围绕三大奇幻题材进行集中整理与推荐，让“想看某类奇幻番剧”的需求，一站就能满足。
              </p>
            </div>

            <div className="mt-12 grid grid-cols-1 md:grid-cols-3 gap-5">
              {values.map((v) => (
                <div
                  key={v.title}
                  className="rounded-2xl bg-background-100 border border-background-200 p-6 hover:border-primary-300 transition-colors"
                >
                  <span className="w-12 h-12 rounded-xl bg-primary-500 text-background-50 flex items-center justify-center text-2xl">
                    <i className={v.icon}></i>
                  </span>
                  <h3 className="mt-4 font-heading text-xl text-foreground-950">{v.title}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{v.desc}</p>
                </div>
              ))}
            </div>

            <div className="mt-12 rounded-2xl bg-secondary-50 border border-secondary-200 p-6 md:p-8">
              <h2 className="font-heading text-2xl text-foreground-950 mb-4">内容原则</h2>
              <ul className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {principles.map((p, i) => (
                  <li key={p} className="flex items-center gap-3 text-sm text-secondary-900">
                    <span className="w-6 h-6 shrink-0 rounded-full bg-secondary-500 text-background-50 flex items-center justify-center text-xs">
                      {i + 1}
                    </span>
                    {p}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <p className="text-sm text-accent-600 font-medium inline-flex items-center gap-2">
              <i className="ri-heart-3-line"></i>
              用户口碑
            </p>
            <h2 className="mt-2 font-heading text-3xl text-foreground-950">大家怎么说</h2>
            <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
              {reviews.map((r) => (
                <figure
                  key={r.name}
                  className="rounded-2xl bg-background-50 border border-background-200 p-6"
                >
                  <div className="flex text-accent-500">
                    {[1, 2, 3, 4, 5].map((s) => (
                      <i key={s} className="ri-star-fill"></i>
                    ))}
                  </div>
                  <blockquote className="mt-4 text-sm text-foreground-700 leading-relaxed">
                    “{r.text}”
                  </blockquote>
                  <figcaption className="mt-5 flex items-center gap-3">
                    <img
                      src={r.avatar}
                      alt={`${r.name} 头像`}
                      className="w-11 h-11 rounded-full object-cover"
                    />
                    <div>
                      <p className="text-sm font-medium text-foreground-950">{r.name}</p>
                      <p className="text-xs text-foreground-500">{r.role}</p>
                    </div>
                  </figcaption>
                </figure>
              ))}
            </div>
          </div>
        </section>

        <section className="py-14 md:py-20 bg-background-50">
          <div className="max-w-7xl mx-auto px-4 md:px-6">
            <div className="flex items-end justify-between mb-8">
              <div>
                <p className="text-sm text-primary-600 font-medium inline-flex items-center gap-2">
                  <i className="ri-question-line"></i>
                  常见问题
                </p>
                <h2 className="mt-2 font-heading text-3xl text-foreground-950">你可能想问</h2>
              </div>
              <Link to="/" className="hidden sm:inline-flex items-center gap-1 text-sm text-primary-600">
                返回首页 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
              {faqs.slice(0, 3).map((f) => (
                <div key={f.q} className="rounded-2xl bg-background-100 border border-background-200 p-6">
                  <span className="w-8 h-8 rounded-lg bg-primary-100 text-primary-600 flex items-center justify-center">
                    <i className="ri-question-line"></i>
                  </span>
                  <h3 className="mt-3 font-medium text-foreground-950">{f.q}</h3>
                  <p className="mt-2 text-sm text-foreground-600 leading-relaxed line-clamp-3">{f.a}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="contact" className="py-14 md:py-20 bg-background-100">
          <div className="max-w-7xl mx-auto px-4 md:px-6 grid grid-cols-1 lg:grid-cols-2 gap-10">
            <div>
              <p className="text-sm text-accent-600 font-medium inline-flex items-center gap-2">
                <i className="ri-mail-send-line"></i>
                联系我们
              </p>
              <h2 className="mt-2 font-heading text-3xl text-foreground-950">投稿与反馈</h2>
              <p className="mt-4 text-foreground-600 leading-relaxed">
                如果你有想推荐的妖精、精灵或妖怪题材番剧，或对本站有任何建议，欢迎留言告诉我们。我们会认真阅读每一条反馈，不断把聚合内容做得更好。
              </p>
              <ul className="mt-6 space-y-3 text-sm text-foreground-600">
                {categories.map((c) => (
                  <li key={c.type} className="flex items-center gap-3">
                    <span className="w-8 h-8 rounded-lg bg-secondary-100 text-secondary-700 flex items-center justify-center">
                      <i className={c.icon}></i>
                    </span>
                    <Link to={`/category/${c.type}`} className="hover:text-primary-600">
                      {c.name}：浏览{categoryLabel(c.type)}推荐
                    </Link>
                  </li>
                ))}
              </ul>
              <p className="mt-6 text-sm text-foreground-500 inline-flex items-center gap-2">
                <i className="ri-mail-line"></i>
                contact@yaojing-anime.example
              </p>
            </div>

            <form
              data-readdy-form
              onSubmit={onSubmit}
              className="rounded-2xl bg-background-50 border border-background-200 p-6 md:p-8"
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-foreground-900 mb-1.5">
                    称呼
                  </label>
                  <input
                    id="name"
                    name="name"
                    type="text"
                    required
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-foreground-900 mb-1.5">
                    联系方式
                  </label>
                  <input
                    id="email"
                    name="email"
                    type="email"
                    required
                    placeholder="你的邮箱"
                    className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400"
                  />
                </div>
              </div>

              <div className="mt-4">
                <label htmlFor="subject" className="block text-sm font-medium text-foreground-900 mb-1.5">
                  主题
                </label>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  placeholder="如：番剧投稿 / 建议"
                  className="w-full h-11 px-4 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400"
                />
              </div>

              <div className="mt-4">
                <label htmlFor="message" className="block text-sm font-medium text-foreground-900 mb-1.5">
                  留言
                </label>
                <textarea
                  id="message"
                  name="message"
                  required
                  maxLength={500}
                  rows={5}
                  placeholder="写下你的推荐或建议（最多500字）"
                  className="w-full px-4 py-3 rounded-xl bg-background-100 border border-background-200 text-sm text-foreground-900 outline-none focus:border-primary-400 resize-none"
                ></textarea>
                <p className="mt-1 text-xs text-foreground-400">最多 500 字</p>
              </div>

              <input
                type="text"
                name="company_alt"
                className="hp-field"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
              />

              <button
                type="submit"
                disabled={state === "loading"}
                className="mt-6 w-full h-12 rounded-full bg-primary-500 hover:bg-primary-600 disabled:opacity-60 text-background-50 text-sm font-medium transition-colors whitespace-nowrap cursor-pointer"
              >
                {state === "loading" ? "发送中..." : "提交留言"}
              </button>

              {state === "success" && (
                <p className="mt-4 text-sm text-secondary-700 bg-secondary-50 border border-secondary-200 rounded-xl px-4 py-3 inline-flex items-center gap-2 w-full">
                  <i className="ri-checkbox-circle-line"></i>
                  {msg}
                </p>
              )}
              {state === "error" && (
                <p className="mt-4 text-sm text-accent-700 bg-accent-50 border border-accent-200 rounded-xl px-4 py-3 inline-flex items-center gap-2 w-full">
                  <i className="ri-error-warning-line"></i>
                  {msg}
                </p>
              )}
            </form>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );

  function categoryLabel(type: string) {
    return categories.find((c) => c.type === type)?.name || "";
  }
}